/**
 * Global country popup (first visit + floating reopen button).
 * Uses theme settings rendered by snippets/country-popup.liquid.
 */
(function () {
  /**
   * @returns {string[]}
   */
  function getDeliveryFeeHandles() {
    const body = document.body;
    if (!(body instanceof HTMLElement)) return [];

    const handles = [
      String(body.getAttribute('data-ccw-delivery-fee-product-handle-default') || '').trim(),
      String(body.getAttribute('data-ccw-delivery-fee-product-handle-uae') || '').trim(),
      String(body.getAttribute('data-ccw-delivery-fee-product-handle') || '').trim(),
    ].filter(Boolean);

    // De-dupe while preserving order.
    return Array.from(new Set(handles));
  }

  /**
   * @returns {Promise<any|null>}
   */
  async function fetchCart() {
    try {
      const res = await fetch('/cart.js', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  }

  /**
   * @param {string} lineKey
   * @returns {Promise<void>}
   */
  async function removeCartLine(lineKey) {
    const key = String(lineKey || '').trim();
    if (!key) return;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
    } catch (_) {
      // ignore
    }
  }

  /**
   * Clear the entire cart (all items).
   * Best-effort: ignores failures so market navigation can continue.
   * @returns {Promise<void>}
   */
  async function clearCartBeforeCountrySwitch() {
    // Fast path supported by Shopify.
    try {
      const res = await fetch('/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) return;
    } catch (_) {
      // fall back
    }

    // Fallback: remove line-by-line.
    const cart = await fetchCart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    if (!items.length) return;
    await Promise.all(
      items.map((/** @type {any} */ item) => removeCartLine(String(item?.key || '')))
    );
  }

  /**
   * @param {string} iso
   * @returns {string}
   */
  function isoToFlagEmoji(iso) {
    const code = String(iso || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) return '';
    return String.fromCodePoint(...code.split('').map((c) => 127397 + c.charCodeAt(0)));
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  function isLikelyImageFlag(value) {
    const v = String(value || '').trim().toLowerCase();
    if (!v) return false;
    return /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/.test(v);
  }

  /**
   * Resolve image source for flag values.
   * Loads flag images from theme assets only.
   * @param {string} value
   * @param {string} assetBase
   * @returns {string}
   */
  function resolveFlagImageSrc(value, assetBase) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const base = String(assetBase || '').trim();
    if (!base) return '';

    // Keep only file name so flags are always loaded from theme assets.
    const withoutQuery = raw.split('?')[0] || raw;
    const parts = withoutQuery.split('/').filter(Boolean);
    const filename = parts.length ? parts[parts.length - 1] : withoutQuery;
    return `${base}${filename}`;
  }

  /**
   * @param {string} flag
   * @param {string} iso
   * @returns {string}
   */
  function resolveTextFlag(flag, iso) {
    const raw = String(flag || '').trim();
    if (!raw) return isoToFlagEmoji(iso) || '🌐';
    if (/^[A-Za-z]{2}$/.test(raw)) return isoToFlagEmoji(raw) || raw.toUpperCase();
    return raw;
  }

  /**
   * @param {unknown} rawItem
   * @param {string} [fallbackIso]
   * @returns {{name: string, flag: string, url: string, iso: string} | null}
   */
  function normalizeCountryItem(rawItem, fallbackIso = '') {
    const src = rawItem && typeof rawItem === 'object' ? /** @type {Record<string, unknown>} */ (rawItem) : {};
    const iso = String(src.iso || src.code || fallbackIso || '').trim().toUpperCase();
    const name = String(src.name || src.country || src.label || iso || '').trim();
    const flag = String(src.flag || src.emoji || src.icon || '').trim();
    const url = String(src.url || src.link || src.href || '').trim();
    if (!name) return null;
    return { name, flag, url, iso };
  }

  /**
   * @param {string} raw
   * @returns {Array<{name: string, flag: string, url: string, iso: string}>}
   */
  function parseCountryItems(raw) {
    const text = String(raw || '').trim();
    if (!text) return [];

    /** @type {unknown[]} */
    const attempts = [
      text,
      text.replace(/[“”]/g, '"').replace(/[‘’]/g, "'"),
      text.replace(/[“”]/g, '"').replace(/[‘’]/g, "'").replace(/'/g, '"'),
    ];

    for (const candidate of attempts) {
      try {
        const parsed = JSON.parse(String(candidate));
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => normalizeCountryItem(item))
            .filter((item) => item !== null);
        }
        if (parsed && typeof parsed === 'object') {
          return Object.entries(parsed)
            .map(([key, value]) => normalizeCountryItem(value, key))
            .filter((item) => item !== null);
        }
      } catch (_) {
        // try next candidate
      }
    }

    return [];
  }

  /**
   * @param {HTMLElement} root
   * @param {Array<{name: string, flag: string, url: string, iso: string}>} items
   * @param {string} currentIso
   * @param {string} assetBase
   */
  function renderCountryButtons(root, items, currentIso, assetBase) {
    const list = root.querySelector('[data-country-popup-list]');
    if (!(list instanceof HTMLElement)) return;
    list.innerHTML = '';

    if (!items.length) {
      const hint = document.createElement('p');
      hint.className = 'country-popup__hint';
      hint.textContent = 'No valid countries found. Use JSON: [{"iso":"LB","name":"Lebanon","flag":"🇱🇧","url":"/"}]';
      list.appendChild(hint);
      return;
    }

    items.forEach((item) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'country-popup__item';
      btn.setAttribute('data-country-popup-url', item.url);
      if (item.iso) btn.setAttribute('data-country-popup-iso', item.iso);
      if (!item.url) btn.disabled = true;
      if (item.iso && currentIso && item.iso === currentIso.toUpperCase()) {
        btn.classList.add('is-current');
      }

      let flag;
      if (isLikelyImageFlag(item.flag)) {
        const img = document.createElement('img');
        img.className = 'country-popup__flag-img';
        img.src = resolveFlagImageSrc(item.flag, assetBase);
        img.alt = '';
        img.loading = 'lazy';
        img.setAttribute('aria-hidden', 'true');
        flag = img;
      } else {
        const span = document.createElement('span');
        span.className = 'country-popup__flag';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = resolveTextFlag(item.flag, item.iso);
        flag = span;
      }

      const name = document.createElement('span');
      name.className = 'country-popup__name';
      name.textContent = item.name;

      btn.append(flag, name);
      list.appendChild(btn);
    });
  }

  /**
   * Render current selected flag into floating button.
   * @param {HTMLElement} root
   * @param {Array<{name: string, flag: string, url: string, iso: string}>} items
   * @param {string} currentIso
   * @param {string} assetBase
   */
  function renderFabFlag(root, items, currentIso, assetBase) {
    const fab = root.querySelector('[data-country-popup-fab]');
    if (!(fab instanceof HTMLButtonElement)) return;

    const iconHost = fab.querySelector('.country-popup__fab-icon');
    if (!(iconHost instanceof HTMLElement)) return;

    const normalizedCurrentIso = String(currentIso || '').trim().toUpperCase();
    const selectedItem =
      items.find((item) => item.iso && item.iso.toUpperCase() === normalizedCurrentIso) || null;

    const selectedFlag = selectedItem ? selectedItem.flag : '';
    const selectedIso = selectedItem ? selectedItem.iso : normalizedCurrentIso;

    if (isLikelyImageFlag(selectedFlag)) {
      const src = resolveFlagImageSrc(selectedFlag, assetBase);
      if (src) {
        iconHost.innerHTML = '';
        const img = document.createElement('img');
        img.className = 'country-popup__fab-flag-img';
        img.src = src;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        iconHost.appendChild(img);
        return;
      }
    }

    iconHost.textContent = resolveTextFlag(selectedFlag, selectedIso);
  }

  /**
   * @param {HTMLElement} root
   */
  function wireCountryPopup(root) {
    const openButtons = Array.from(root.querySelectorAll('[data-country-popup-open]')).filter(
      (el) => el instanceof HTMLButtonElement
    );
    const dialog = root.querySelector('[data-country-popup-dialog]');
    const cancelBtn = root.querySelector('[data-country-popup-cancel]');
    const currentIso = String(root.getAttribute('data-country-popup-current-iso') || '').trim();
    const autoOpenFirstVisit = String(
      root.getAttribute('data-country-popup-auto-open-first-visit') || ''
    ).trim() === 'true';
    const seenStorageKey = String(
      root.getAttribute('data-country-popup-storage-key') || 'ccw-country-popup-seen'
    ).trim();
    const rawCountryItems = String(root.getAttribute('data-country-popup-items-json') || '').trim();
    const assetBase = String(root.getAttribute('data-country-popup-asset-base') || '').trim();

    if (openButtons.length === 0) return;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!(cancelBtn instanceof HTMLButtonElement)) return;

    const parsedItems = parseCountryItems(rawCountryItems);
    renderCountryButtons(root, parsedItems, currentIso, assetBase);
    renderFabFlag(root, parsedItems, currentIso, assetBase);

    const countryButtons = Array.from(
      root.querySelectorAll('[data-country-popup-url]')
    ).filter((el) => el instanceof HTMLButtonElement);

    const closeDialog = () => {
      if (dialog.open) dialog.close();
    };

    const hasSeenPopup = () => {
      try {
        return globalThis.localStorage.getItem(seenStorageKey) === '1';
      } catch (_) {
        return false;
      }
    };

    const markPopupSeen = () => {
      try {
        globalThis.localStorage.setItem(seenStorageKey, '1');
      } catch (_) {
        // ignore
      }
    };

    const showDialog = () => {
      try {
        dialog.showModal();
      } catch (_) {
        dialog.setAttribute('open', '');
      }
    };

    openButtons.forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      btn.addEventListener('click', () => {
        showDialog();
        markPopupSeen();
      });
    });

    cancelBtn.addEventListener('click', closeDialog);
    dialog.addEventListener('close', markPopupSeen);

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    countryButtons.forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      const itemIso = String(btn.getAttribute('data-country-popup-iso') || '').trim();
      const itemUrl = String(btn.getAttribute('data-country-popup-url') || '').trim();

      btn.addEventListener('click', async () => {
        if (!itemUrl) return;
        if (itemIso && currentIso && itemIso.toUpperCase() === currentIso.toUpperCase()) {
          closeDialog();
          return;
        }
        markPopupSeen();
        btn.disabled = true;
        try {
          await clearCartBeforeCountrySwitch();
        } catch (_) {
          // ignore and continue navigation
        } finally {
          globalThis.location.href = itemUrl;
        }
      });
    });

    const openPopup = () => {
  showDialog();
  markPopupSeen();
};

const initDelay = Number(

  root.getAttribute('data-country-popup-init-delay') || 0

);

if (autoOpenFirstVisit && !hasSeenPopup()) {
  if (initDelay > 0) {
    setTimeout(() => {
      openPopup();
    }, initDelay * 1000);
  } else {
    openPopup();
  }
}
  }

  document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('[data-country-popup-root]');
    if (!(root instanceof HTMLElement)) return;
    wireCountryPopup(root);
  });
})();

